import { ApiExercise, CreateExerciseRequest, PagedExerciseResponse } from '@codey2/codey-api-types/models';
import { codeyApiClient } from './codeyApiClient';
import { usePrivateApi } from './usePrivateApi';
import { useCallback, useState } from 'react';

export function useExercisesApi() {
  const [isLoading, setIsLoading] = useState(false);
  const { doPost, doDelete, doPut } = usePrivateApi();

  const getExercises = useCallback(async (page: number): Promise<PagedExerciseResponse> => {
    try {
      setIsLoading(true);
      const result = await codeyApiClient.get<PagedExerciseResponse>(`/exercises/public?page=${page}&pagesize=10`);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getExerciseById = useCallback(async (id: number): Promise<ApiExercise> => {
    try {
      setIsLoading(true);
      const result = await codeyApiClient.get<ApiExercise>(`/exercises/public/${id}`);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createExercise = useCallback(
    async (input: CreateExerciseRequest): Promise<ApiExercise> => {
      try {
        setIsLoading(true);
        return await doPost<CreateExerciseRequest, ApiExercise>('/exercises', input);
      } finally {
        setIsLoading(false);
      }
    },
    [doPost]
  );

  const updateExercise = useCallback(
    async (id: number, input: CreateExerciseRequest): Promise<ApiExercise> => {
      try {
        setIsLoading(true);
        return await doPut<CreateExerciseRequest, ApiExercise>(`/exercises/${id}`, input);
      } finally {
        setIsLoading(false);
      }
    },
    [doPut]
  );

  const deleteExercise = useCallback(
    async (id: number): Promise<void> => {
      try {
        setIsLoading(true);
        return await doDelete(`/exercises/${id}`);
      } finally {
        setIsLoading(false);
      }
    },
    [doDelete]
  );

  return {
    getExercises,
    getExerciseById,
    createExercise,
    updateExercise,
    deleteExercise,
    isLoading,
  };
}
